import {
  ActionButton,
  ResultsTable,
  TableCell,
  TableRow,
  Typography,
  Link,
  useClientOptions,
  useDialogs,
  Hidden,
  TransferToLocationDialog
} from '@paintscout/ui'
import type { TableCellProps, ResultsTableProps } from '@paintscout/ui'
import type { ClientMetaDocument } from 'paintscout'
import React, { useCallback } from 'react'
import moment from 'moment'
import { formatCurrency, getObjectLabels } from '@paintscout/util/builder'
import { escapeHref } from '@paintscout/util'
import AssignmentIcon from '@material-ui/icons/Assignment'
import SettingsIcon from '@material-ui/icons/Settings'
import PeopleIcon from '@material-ui/icons/People'
import SwapHorizIcon from '@material-ui/icons/SwapHoriz'

export interface LocationSearchTableProps
  extends Omit<ResultsTableProps, 'rows' | 'columns' | 'classes' | 'page' | 'renderRow'> {
  locations: Partial<ClientMetaDocument>[]
  onLocationClick?: (location: Partial<ClientMetaDocument>) => any
  onLocationDeleted?: () => any
  onFetchNextPage: (nextPage: number) => any
  page: number
  sort?: string

  onRequestSort?: (ev: React.MouseEvent, name: string, direction: 'asc' | 'desc') => void
}

export default function LocationSearchTable({
  locations,
  onFetchNextPage,
  onPageChange,
  onLocationClick,
  onRequestSort,
  page,
  sort,
  ...other
}: LocationSearchTableProps) {
  const { options } = useClientOptions()
  const objectLabels = getObjectLabels({ options })
  const { openDialog, dismissDialog } = useDialogs()
  const createSortHandler = (property: string) => (event: React.MouseEvent) => {
    if (onRequestSort) {
      // get current sort direction
      const currentSort = getSortDirection(property) || 'asc'

      // flip the sort direction
      const newSort = currentSort === 'asc' ? 'desc' : 'asc'

      onRequestSort(event, property, newSort)
    }
  }

  const getSortDirection = (property: string) => {
    const currentSortDirection = sort && sort.startsWith('-') ? 'desc' : 'asc'
    const currentSortName = sort && sort.startsWith('-') ? sort.slice(1) : sort

    if (currentSortName === property) {
      return currentSortDirection
    }
  }

  const handleAction = useCallback((action: string, rows: any[]) => {
    let url = ''
    if (action === 'transfer') {
      openDialog(TransferToLocationDialog, {
        locationName: rows[0].name,
        locationId: rows[0]._id,
        onConfirm: dismissDialog,
        onCancel: dismissDialog
      })
    } else {
      switch (action) {
        case 'estimates':
          url = `/estimates`
          break
        case 'settings':
          url = `/settings`
          break
        case 'users':
          url = `/settings?section=billing-users`
          break
        default:
          url = '/dashboard'
          break
      }

      window.localStorage.setItem('masqueradeCompanyId', rows[0]._id)
      window.location.href = escapeHref(url)
    }
  }, [])

  const renderRow = useCallback((row) => {
    // render cell as a link unless onLocationClick is defined
    const cellProps = {
      row,
      disableLink: !!onLocationClick,
      style: onLocationClick ? { cursor: 'pointer' } : undefined,
      onClick: onLocationClick
        ? () => {
            // prevent link navigation
            // e.preventDefault()

            onLocationClick(row)
          }
        : null
    }

    const rowActions = [
      {
        key: 'estimates',
        label: `${objectLabels.quote.plural}`,
        icon: AssignmentIcon
      },
      {
        key: 'settings',
        label: 'Settings',
        icon: SettingsIcon
      },
      {
        key: 'users',
        label: 'Users',
        icon: PeopleIcon
      },
      {
        key: 'transfer',
        label: 'Transfer',
        icon: SwapHorizIcon
      }
    ]

    return (
      <TableRow key={row._id}>
        <TableCellLink {...cellProps}>
          <Typography variant={'body1'}>{row.name}</Typography>
          <Typography variant={'subtitle2'}>{row._id}</Typography>
        </TableCellLink>
        <Hidden smDown>
          <TableCellLink {...cellProps}>{row.city}</TableCellLink>
        </Hidden>
        <Hidden smDown>
          <TableCellLink {...cellProps}>{row.province}</TableCellLink>
        </Hidden>
        <Hidden smDown>
          <TableCellLink align="center" {...cellProps}>
            {row.userCounts?.all ?? ''}
          </TableCellLink>
        </Hidden>
        <Hidden mdDown>
          <TableCellLink align="center" {...cellProps}>
            {row.created ? moment(row.created).format('YYYY-MM-DD') : ''}
          </TableCellLink>
        </Hidden>
        <Hidden smDown>
          <TableCellLink align="center" {...cellProps}>
            {row.aggregates?.totalQuotes ?? 0}
          </TableCellLink>
        </Hidden>
        <TableCellLink align="center" {...cellProps}>
          {row.aggregates?.closedValue ? formatCurrency({ options, value: row.aggregates.closedValue }) : ''}
        </TableCellLink>
        <TableCellLink align="center" {...cellProps}>
          {row.aggregates?.closeRate ? `${row.aggregates?.closeRate.toFixed(2)}%` : ''}
        </TableCellLink>
        <TableCellLink align="center" {...cellProps}>
          <ActionButton
            data-testid="actions"
            actions={rowActions}
            onActionClick={(ev, action) => handleAction(action, [row])}
          />
        </TableCellLink>
      </TableRow>
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <ResultsTable
      rows={locations || []}
      noResultsMessage="No results"
      page={page}
      columns={
        <TableRow>
          <TableCell>Name</TableCell>
          <Hidden smDown>
            <TableCell>City</TableCell>
          </Hidden>
          <Hidden smDown>
            <TableCell>State</TableCell>
          </Hidden>
          <Hidden smDown>
            <TableCell
              sortable={!!sort}
              sortDirection={getSortDirection('userCounts.all')}
              onClick={createSortHandler('userCounts.all')}
            >
              User #
            </TableCell>
          </Hidden>
          <Hidden mdDown>
            <TableCell
              sortable={!!sort}
              sortDirection={getSortDirection('created')}
              onClick={createSortHandler('created')}
            >
              Sign Up Date
            </TableCell>
          </Hidden>
          <Hidden smDown>
            <TableCell
              sortable={!!sort}
              sortDirection={getSortDirection('aggregates.totalQuotes')}
              onClick={createSortHandler('aggregates.totalQuotes')}
            >
              Total {objectLabels.quote.value}
            </TableCell>
          </Hidden>
          <TableCell
            sortable={!!sort}
            sortDirection={getSortDirection('aggregates.closedValue')}
            onClick={createSortHandler('aggregates.closedValue')}
          >
            Dollars Won
          </TableCell>
          <TableCell
            sortable={!!sort}
            sortDirection={getSortDirection('aggregates.closeRate')}
            onClick={createSortHandler('aggregates.closeRate')}
          >
            Win Rate
          </TableCell>
          <TableCell />
        </TableRow>
      }
      renderRow={renderRow}
      onPageChange={(nextPage) => {
        if (nextPage > page && locations.length < other.rowsPerPage * nextPage) {
          onFetchNextPage(nextPage)
        }

        if (onPageChange) {
          onPageChange(nextPage)
        }
      }}
      {...other}
    />
  )
}

function TableCellLink({
  row,
  disableLink,
  ...other
}: { row: Partial<ClientMetaDocument>; disableLink?: boolean } & TableCellProps) {
  return (
    <TableCell
      component={
        !disableLink
          ? (cProps: any) => <Link to={{ pathname: `/locations/${encodeURIComponent(row._id)}` }} {...cProps} />
          : undefined
      }
      {...other}
    />
  )
}
